/**
 * Module dependencies.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ListFilter, ListFilters } from './form-filters-type';
import styles from '../crud.module.less';
import { isUndefined, merge, omitBy } from 'lodash';
import { WrapperField } from './wrapper-field';
import { useSearchParams } from 'react-router-dom';
import { Expanded } from '../../table/expanded';
import { Button } from 'app/components/atoms/button/button';
import { ControlOutlined } from '@ant-design/icons';
import { IoFilter } from 'react-icons/io5';
import { useQueryParamsStorageProvider } from 'app/components/providers/query-params-storage/query-params-storage';

/**
 * `Props` type.
 */

type Props = {
  filters: ListFilters;
  appendFilters: (values: any) => void;
  children: JSX.Element;
  queryStorageKey?: string | undefined | null;
  hasNested: boolean;
  defaultQueryString: string | undefined;
  inlineFiltersExpanded?: boolean;
};

/**
 * Export `FiltersForm` component.
 */

export function FiltersForm(props: Props): JSX.Element {
  const { filters, hasNested, queryStorageKey, defaultQueryString, appendFilters, children, inlineFiltersExpanded } =
    props;
  const [searchParams, setSearchParams] = useSearchParams(defaultQueryString);
  const { getPageParams } = useQueryParamsStorageProvider();
  const lastFilterRef = useRef<string | null>(null);

  const submitFilters = useCallback(
    (values: any, showFilters?: boolean) => {
      const { __liveMode, page: _page, pageSize: _pageSize, order: _order, ...filterValues } = values;
      const cleanValues = omitBy(filterValues, value => isUndefined(value));
      const newFilter = JSON.stringify(cleanValues);

      setSearchParams(searchParams => {
        searchParams.set('filter', newFilter);
        searchParams.set('filterOpened', String(showFilters));

        // Reset pagination to page 1 only when filters actually change.
        // On initial mount (null), just record the filter without resetting the page
        // so URL page params (e.g. ?page=5) are preserved on navigation.
        if (lastFilterRef.current === null) {
          lastFilterRef.current = newFilter;
        } else if (lastFilterRef.current !== newFilter) {
          searchParams.set('page', '1');
          lastFilterRef.current = newFilter;
        }

        if (__liveMode) {
          searchParams.set('liveMode', 'true');
        } else {
          searchParams.delete('liveMode');
        }

        return searchParams;
      });

      appendFilters({ filters: cleanValues, liveMode: __liveMode || false });
    },
    [appendFilters, setSearchParams]
  );

  const toggleInlineFilters = useCallback(() => {
    setShowInlineFilters(prev => {
      const newValue = !prev;
      setSearchParams(searchParams => {
        searchParams.set('inlineFiltersOpened', String(newValue));
        return searchParams;
      });
      return newValue;
    });
  }, [setSearchParams]);

  const [initialValues] = useState<any>(() => {
    const { filters, ...rest } = getPageParams(queryStorageKey) ?? {};
    let result2 = { ...filters, ...rest };

    if (searchParams.has('filter')) {
      const filter = searchParams.get('filter');

      result2 = merge(result2, { ...JSON.parse(filter ?? '{}') });
    }

    // Remove pagination parameters from initial values - they are handled separately
    const { page: _page, pageSize: _pageSize, order: _order, ...cleanResult } = result2;

    // Restore live mode from URL
    if (searchParams.has('liveMode')) {
      cleanResult.__liveMode = searchParams.get('liveMode') === 'true';
    }

    //submitFilters(cleanResult);

    return cleanResult;
  });

  const [showFilters, setShowFilters] = useState(searchParams.get('filterOpened') === 'true');
  const [showInlineFilters, setShowInlineFilters] = useState(() => {
    if (searchParams.has('inlineFiltersOpened')) {
      return searchParams.get('inlineFiltersOpened') === 'true';
    }

    // Default to open if there are restored active filters
    const filterNames = filters.filter(f => f.type !== 'liveToggle').map(f => ('name' in f ? f.name : ''));
    const hasRestoredFilters = filterNames.some(name => {
      const value = initialValues[name];
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });

    if (hasRestoredFilters) return true;

    return inlineFiltersExpanded ?? false;
  });
  const form = useForm({ mode: 'all', reValidateMode: 'onChange' });
  const { watch } = form;
  const [values, setValues] = useState<any>(initialValues);
  const collapsedFilters = useMemo(() => filters.filter(item => !item.inline), [filters]);
  const inlineFilters = useMemo(() => filters.filter(item => !!item.inline), [filters]);
  const searchFilters = useMemo(() => inlineFilters.filter(item => item.type === 'inputSearch'), [inlineFilters]);
  const otherInlineFilters = useMemo(() => inlineFilters.filter(item => item.type !== 'inputSearch'), [inlineFilters]);
  const hasSearchField = searchFilters.length > 0;
  const hasOtherInlineFilters = otherInlineFilters.length > 0;

  // Check if there are any active filters
  const hasActiveFilters = useMemo(() => {
    if (!values) return false;

    // Get all filter field names (exclude LiveToggle as it doesn't have a name)
    const filterNames = filters.filter(f => f.type !== 'liveToggle').map(f => ('name' in f ? f.name : ''));

    // Check if any filter has a non-empty value
    return filterNames.some(name => {
      const value = values[name];
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
  }, [values, filters]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    const filterNames = filters.filter(f => f.type !== 'liveToggle').map(f => ('name' in f ? f.name : ''));
    filterNames.forEach(name => {
      form.setValue(name, undefined);
    });
  }, [filters, form]);

  useEffect(() => {
    const subscription = watch(setValues);

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (hasNested || !initialValues) {
      return;
    }

    for (const key of Object.keys(initialValues)) {
      if (initialValues[key] !== undefined && initialValues[key] !== null) {
        form.setValue(key, initialValues[key]);
      }
    }
  }, [form, hasNested, initialValues]);

  const lastValuesRef = useRef<string>('');

  useEffect(() => {
    if (hasNested) {
      return;
    }

    const valuesString = JSON.stringify(values);

    // Skip if values haven't changed
    if (lastValuesRef.current === valuesString) {
      return;
    }

    const timer = setTimeout(() => {
      clearTimeout(timer);

      lastValuesRef.current = valuesString;
      submitFilters(values, showFilters);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appendFilters, hasNested, showFilters, values]);

  useEffect(() => {
    if (hasNested) {
      return;
    }

    const data = form.getValues();

    setValues(data);
  }, [form, hasNested]);

  return (
    <>
      <div className={styles.filterForm}>
        {/* First row: Search field + Toggle button + Action buttons */}
        {hasSearchField && (
          <div className={styles.searchRow}>
            <div className={styles.searchField}>
              {!hasNested &&
                searchFilters.map((item: ListFilter, index: number) => {
                  return <WrapperField form={form} item={item} key={index} values={values} />;
                })}
            </div>

            <div className={styles.searchRowActions}>
              {hasOtherInlineFilters && (
                <button
                  className={styles.inlineFilterToggle}
                  disabled={hasActiveFilters}
                  onClick={toggleInlineFilters}
                  type={'button'}
                >
                  <IoFilter size={20} />
                </button>
              )}

              <div className={styles.filterActions}>
                {collapsedFilters.length > 0 && (
                  <Button icon={<ControlOutlined />} onClick={() => setShowFilters(!showFilters)} />
                )}

                {children}
              </div>
            </div>
          </div>
        )}

        {/* Fallback to old layout if no search field */}
        {!hasSearchField && (
          <div className={styles.filtersRow}>
            <div className={styles.filterWidgets}>
              {!hasNested &&
                inlineFilters.map((item: ListFilter, index: number) => {
                  return <WrapperField form={form} item={item} key={index} values={values} />;
                })}
            </div>

            <div className={styles.filterActions}>
              {collapsedFilters.length > 0 && (
                <Button icon={<ControlOutlined />} onClick={() => setShowFilters(!showFilters)} />
              )}

              {children}
            </div>
          </div>
        )}
      </div>

      {/* Expandable inline filters (when search exists) */}
      {hasSearchField && hasOtherInlineFilters && (
        <Expanded expanded={showInlineFilters}>
          <div className={styles.inlineFiltersContainer}>
            <div className={styles.inlineFiltersWrapper}>
              {!hasNested &&
                otherInlineFilters.map((item: ListFilter, index: number) => {
                  return <WrapperField form={form} item={item} key={index} values={values} />;
                })}
            </div>
            {hasActiveFilters && (
              <div className={styles.clearAllButton}>
                <Button onClick={clearAllFilters}>{'Clear All'}</Button>
              </div>
            )}
          </div>
        </Expanded>
      )}

      {/* Collapsed filters panel */}
      <Expanded expanded={showFilters}>
        <div className={styles.collapsedWrapper}>
          {!hasNested &&
            collapsedFilters.map((item: ListFilter, index: number) => {
              return <WrapperField form={form} item={item} key={index} values={values} />;
            })}
        </div>
      </Expanded>
    </>
  );
}
